// frontend/src/pages/HomePage.js
//
// The Śāstric Oracle — home page.
// Full-screen mahāvākya hero, oracle search, verse of the moment,
// 9-category taxonomy grid, site footer with app download badges.

import { useState, useEffect } from 'react';
import SearchBar from '../components/search/SearchBar';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';

const MAHAVAKYAS = [
  { deva:'प्रज्ञानं ब्रह्म',   iast:'prajñānaṃ brahma',   english:'Consciousness is Brahman',  source:'Aitareya Upaniṣad · Ṛgveda' },
  { deva:'अहं ब्रह्मास्मि',    iast:'ahaṃ brahmāsmi',    english:'I am Brahman',               source:'Bṛhadāraṇyaka Upaniṣad · Yajurveda' },
  { deva:'तत् त्वम् असि',      iast:'tat tvam asi',       english:'That thou art',              source:'Chāndogya Upaniṣad · Sāmaveda' },
  { deva:'अयम् आत्मा ब्रह्म', iast:'ayam ātmā brahma',  english:'This Self is Brahman',       source:'Māṇḍūkya Upaniṣad · Atharvaveda' },
];

const VERSE_POOL = [
  { deva:'कर्मण्येवाधिकारस्ते मा फलेषु कदाचन ।\nमा कर्मफलहेतुर्भूर्मा ते सङ्गोऽस्त्वकर्मणि ॥', iast:'karmaṇyevādhikāraste mā phaleṣu kadācana |', trans:'You have a right only to perform your duty, never to its fruits. Let not the fruits of action be your motive, nor let your attachment be to inaction.', src:'Bhagavad Gītā · 2.47', trad:'advaita', id:'BG#01#2_47' },
  { deva:'नायमात्मा प्रवचनेन लभ्यो न मेधया न बहुना श्रुतेन ।', iast:'nāyamātmā pravacanena labhyo na medhayā |', trans:'This Self is not attained by study, nor by the intellect, nor by much hearing. By him alone is It attained whom It chooses.', src:'Kaṭha Upaniṣad · 1.2.23', trad:'advaita', id:null },
  { deva:'ब्रह्म सत्यं जगन्मिथ्या जीवो ब्रह्मैव नापरः ।', iast:'brahma satyaṃ jaganmithyā jīvo brahmaiva nāparaḥ |', trans:'Brahman alone is real; the world is appearance; the individual self is none other than Brahman itself.', src:'Vivekacūḍāmaṇi · 20', trad:'advaita', id:null },
  { deva:'सर्वे भवन्तु सुखिनः सर्वे सन्तु निरामयाः ।\nसर्वे भद्राणि पश्यन्तु मा कश्चिद्दुःखभाग्भवेत् ॥', iast:'sarve bhavantu sukhinaḥ sarve santu nirāmayāḥ |', trans:'May all beings be happy; may all be free from disease; may all experience what is auspicious; may none suffer.', src:'Bṛhadāraṇyaka Upaniṣad', trad:'advaita', id:null },
];

const TAXONOMY = [
  { deva:'वेद',       label:'Vedas',          count:'10,552', catId:'veda' },
  { deva:'उपनिषद्',  label:'Upaniṣads',      count:'4,280',  catId:'upanishad' },
  { deva:'भगवद्गीता',label:'Bhagavad Gītā',  count:'700',    catId:'gita' },
  { deva:'वेदान्त',  label:'Vedānta',         count:'2,140',  catId:'vedanta' },
  { deva:'आदि शंकर', label:'Ādi Śaṅkara',    count:'980',    catId:'shankara' },
  { deva:'इतिहास',   label:'Itihāsa',         count:'26,034', catId:'itihasa' },
  { deva:'पुराण',    label:'Purāṇa',          count:'13,551', catId:'purana' },
  { deva:'काव्य',    label:'Kāvya',           count:'1,031',  catId:'kavya' },
  { deva:'ज्योतिष', label:'Jyotiṣa',         count:'Soon',   catId:'jyotisha', soon:true },
];

function AppleStoreBadge() {
  return (
    <svg width="148" height="44" viewBox="0 0 148 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Download on the App Store">
      <rect width="148" height="44" rx="6" fill="#000"/>
      <rect x=".5" y=".5" width="147" height="43" rx="5.5" stroke="#A6A6A6"/>
      <path d="M20.956 22.637c-.028-3.223 2.63-4.791 2.75-4.864-1.5-2.192-3.83-2.492-4.654-2.519-1.963-.2-3.863 1.17-4.863 1.17-1.017 0-2.556-1.149-4.214-1.116-2.137.033-4.131 1.26-5.228 3.175-2.258 3.908-.576 9.668 1.593 12.832 1.085 1.549 2.354 3.278 4.019 3.217 1.624-.066 2.232-1.035 4.193-1.035 1.943 0 2.513 1.035 4.214.997 1.745-.028 2.843-1.557 3.89-3.12 1.247-1.783 1.754-3.528 1.775-3.617-.039-.015-3.397-1.299-3.475-5.12z" fill="#fff"/>
      <path d="M17.85 13.986c.874-1.088 1.472-2.578 1.307-4.09-1.265.053-2.843.866-3.752 1.928-.812.938-1.535 2.476-1.348 3.93 1.415.107 2.864-.726 3.793-1.768z" fill="#fff"/>
      <text x="36" y="17" fill="#fff" fontFamily="Helvetica Neue,Helvetica,Arial,sans-serif" fontSize="9" letterSpacing=".2">Download on the</text>
      <text x="35" y="32" fill="#fff" fontFamily="Helvetica Neue,Helvetica,Arial,sans-serif" fontSize="16" fontWeight="600" letterSpacing="-.3">App Store</text>
    </svg>
  );
}

function GooglePlayBadge() {
  return (
    <svg width="148" height="44" viewBox="0 0 148 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Get it on Google Play" style={{opacity:0.38}}>
      <rect width="148" height="44" rx="6" fill="#000"/>
      <rect x=".5" y=".5" width="147" height="43" rx="5.5" stroke="#A6A6A6"/>
      <path d="M11 10.5l15 11.5-15 11.5V10.5z" fill="#fff" opacity=".9"/>
      <path d="M11 10.5l9 9-9 2.5z" fill="#EA4335" opacity=".9"/>
      <path d="M11 33.5l9-9-9-2.5z" fill="#FBBC04" opacity=".9"/>
      <path d="M26 22l-6-2-9-9.5L26 22z" fill="#4285F4" opacity=".9"/>
      <path d="M26 22L11 33.5l9-9 6-2.5z" fill="#34A853" opacity=".9"/>
      <text x="36" y="16" fill="#fff" fontFamily="Helvetica Neue,Helvetica,Arial,sans-serif" fontSize="8.5" letterSpacing=".2">GET IT ON</text>
      <text x="35" y="31" fill="#fff" fontFamily="Helvetica Neue,Helvetica,Arial,sans-serif" fontSize="15.5" fontWeight="600" letterSpacing="-.3">Google Play</text>
    </svg>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [mvIdx,     setMvIdx]     = useState(0);
  const [mvVisible, setMvVisible] = useState(true);
  const [verse]                   = useState(() => VERSE_POOL[Math.floor(Math.random() * VERSE_POOL.length)]);

  useEffect(() => {
    const t = setInterval(() => {
      setMvVisible(false);
      setTimeout(() => { setMvIdx(i => (i + 1) % MAHAVAKYAS.length); setMvVisible(true); }, 480);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const mv = MAHAVAKYAS[mvIdx];

  function goTo(i) {
    setMvVisible(false);
    setTimeout(() => { setMvIdx(i); setMvVisible(true); }, 480);
  }

  function handleSearch({ query: q, language: lang } = {}) {
    const trimmed = (q || "").trim();
    if (!trimmed) return;
    navigate(`/search?${new URLSearchParams({ query: trimmed, language: lang || 'auto' })}`);
  }

  function handleTaxonomy(item) {
    if (item.soon) return;
    navigate(`/browse/${item.catId}`);
  }

  return (
    <div className={styles.page}>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.heroRule} />

        <div className={`${styles.mv} ${mvVisible ? styles.mvIn : styles.mvOut}`}>
          <p className={styles.mvDeva}>{mv.deva}</p>
          <p className={styles.mvIast}>{mv.iast}</p>
          <p className={styles.mvEnglish}>{mv.english}</p>
          <p className={styles.mvSource}>{mv.source}</p>
          <div className={styles.mvDots}>
            {MAHAVAKYAS.map((_, i) => (
              <button key={i} className={`${styles.mvDot} ${i === mvIdx ? styles.mvDotOn : ''}`} onClick={() => goTo(i)} aria-label={`Verse ${i+1}`} />
            ))}
          </div>
        </div>

        <div className={styles.vline} />
        <p className={styles.prompt}>Ask anything of the tradition.</p>

        <SearchBar onSearch={handleSearch} loading={false} />

        <div className={styles.scrollHint} aria-hidden="true"><div className={styles.scrollDot}/></div>
      </section>

      {/* ── VERSE OF THE MOMENT ── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <p className={styles.eyebrow}>Verse of the Moment</p>
          <button className={styles.votm} onClick={() => verse.id && navigate(`/verse/${encodeURIComponent(verse.id)}`)} style={{cursor: verse.id ? 'pointer' : 'default'}}>
            <p className={styles.votmDeva}>{verse.deva}</p>
            <p className={styles.votmIast}>{verse.iast}</p>
            <p className={styles.votmTrans}>{verse.trans}</p>
            <div className={styles.votmFoot}>
              <span className={styles.tagSrc}>{verse.src}</span>
              <span className={styles.tagTrad}>{verse.trad}</span>
            </div>
          </button>
        </div>
      </section>

      {/* ── TAXONOMY GRID ── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <p className={styles.eyebrow}>Explore the Tradition</p>
          <p className={styles.sectionSub}>59,268 verses · 4 languages · 9 categories</p>
          <div className={styles.taxGrid}>
            {TAXONOMY.map(item => (
              <button key={item.catId} className={`${styles.taxCard} ${item.soon ? styles.taxCardSoon : ''}`} onClick={() => handleTaxonomy(item)}>
                <p className={`${styles.taxDeva} ${item.tel ? styles.taxTel : ''}`}>{item.deva}</p>
                <p className={styles.taxLabel}>{item.label}</p>
                <p className={styles.taxCount}>{item.count}{!item.soon ? ' verses' : ''}</p>
                <span className={styles.taxArr} aria-hidden="true">→</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerLeft}>
            <span className={styles.footerLogo}>वाक्य</span>
            <span className={styles.footerDot}>·</span>
            <span className={styles.footerTagline}>A Śāstric Oracle</span>
            <span className={styles.footerCorpus}>59,268 verses · 4 languages</span>
          </div>
          <div className={styles.footerRight}>
            <p className={styles.footerBadgeLabel}>Get the App</p>
            <div className={styles.appBadges}>
              <div className={styles.badgeWrap}>
                <span className={styles.badgeLink}><AppleStoreBadge /></span>
                <span className={styles.betaPill}>Beta · TestFlight</span>
              </div>
              <div className={styles.badgeWrap}>
                <GooglePlayBadge />
                <span className={styles.soonPill}>Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <p className={styles.footerCopy}>© 2026 Global Vedic · वाक्य</p>
          <nav className={styles.footerLegal}>
            <button className={styles.footerLegalLink}>Privacy</button>
            <button className={styles.footerLegalLink}>Terms</button>
            <button className={styles.footerLegalLink}>About</button>
            <button className={styles.footerLegalLink}>Contact</button>
          </nav>
        </div>
      </footer>

    </div>
  );
}
